import { useSellerCart } from "@/queries/cart/queries";
import { useAddresses } from "@/queries/customer/addresses";
import { useProfile } from "@/queries/customer/customer";
import { zodResolver } from "@hookform/resolvers/zod";
import type { HttpTypes, StoreCartResponse } from "@medusajs/types";
import { useEffect, useMemo, useRef } from "react";
import { useForm } from "react-hook-form";
import {
  checkoutAddressSchema,
  type CheckoutAddressFormData,
  type InlineAddressData
} from "../validations";

/**
 * Compare key address fields to determine if two addresses are effectively the same.
 * Used to validate that the cart address still matches the referenced customer address.
 */
function addressFieldsMatch(
  cartAddress: HttpTypes.StoreCartAddress | null | undefined,
  customerAddress: HttpTypes.StoreCustomerAddress | null | undefined
): boolean {
  if (!cartAddress || !customerAddress) return false;

  // Compare the key fields that define an address
  return (
    cartAddress.first_name === customerAddress.first_name &&
    cartAddress.last_name === customerAddress.last_name &&
    cartAddress.address_1 === customerAddress.address_1 &&
    cartAddress.city === customerAddress.city &&
    cartAddress.country_code === customerAddress.country_code &&
    cartAddress.postal_code === customerAddress.postal_code
  );
}

/**
 * Convert a cart address to inline address data format.
 * Used when cart has an address that doesn't match any saved customer address.
 */
function cartAddressToInline(
  cartAddress: HttpTypes.StoreCartAddress | null | undefined
): InlineAddressData | undefined {
  if (!cartAddress) return undefined;

  // Only create inline data if we have the minimum required fields
  if (
    !cartAddress.first_name ||
    !cartAddress.last_name ||
    !cartAddress.address_1 ||
    !cartAddress.city ||
    !cartAddress.postal_code ||
    !cartAddress.country_code
  ) {
    return undefined;
  }

  return {
    first_name: cartAddress.first_name,
    last_name: cartAddress.last_name,
    company: cartAddress.company || "",
    address_1: cartAddress.address_1,
    address_2: cartAddress.address_2 || "",
    city: cartAddress.city,
    postal_code: cartAddress.postal_code,
    province: cartAddress.province || "",
    country_code: cartAddress.country_code,
    phone: cartAddress.phone || ""
  };
}

export interface UseCheckoutAddressStateOptions {
  /** Cart IDs being checked out */
  cartData?: StoreCartResponse;
  cartLoading: boolean;
}

export interface UseCheckoutAddressStateReturn {
  /** React Hook Form instance */
  form: ReturnType<typeof useForm<CheckoutAddressFormData>>;
  /** Customer addresses */
  addresses: HttpTypes.StoreCustomerAddress[];
  /** Default shipping address (if any) */
  defaultShippingAddress: HttpTypes.StoreCustomerAddress | undefined;
  /** Default billing address (if any) */
  defaultBillingAddress: HttpTypes.StoreCustomerAddress | undefined;
  /** Whether address data is still loading */
  isLoading: boolean;
  /** Whether customer has no saved addresses */
  hasNoAddresses: boolean;
  /** Selected shipping address object */
  selectedShippingAddress: HttpTypes.StoreCustomerAddress | undefined;
  /** Selected billing address object */
  selectedBillingAddress: HttpTypes.StoreCustomerAddress | undefined;
}

/**
 * Hook for managing checkout address step state
 *
 * Handles:
 * - Loading customer addresses, profile, and cart data
 * - Pre-selecting addresses by matching cart address fields to customer addresses
 * - Falling back to default addresses if no cart address or no match
 * - Showing cart address as "Other" if it doesn't match any saved address
 * - Form state with validation
 * - "Same as shipping" logic
 *
 * @example
 * ```tsx
 * const { form, addresses, isLoading } = useCheckoutAddressState({
 *   cartIds: ['cart-1', 'cart-2']
 * });
 * ```
 */
export function useCheckoutAddressState({
  cartData,
  cartLoading
}: UseCheckoutAddressStateOptions): UseCheckoutAddressStateReturn {
  // Fetch addresses, profile, and first cart (for address metadata)
  const { data: addressesData, isLoading: addressesLoading } = useAddresses();
  const { data: profileData, isLoading: profileLoading } = useProfile();

  const addresses = addressesData?.addresses ?? [];
  const profile = profileData!.customer;

  // Extract cart addresses for matching against customer addresses
  const cartAddresses = useMemo(() => {
    const cart = cartData?.cart;
    return {
      shipping: cart?.shipping_address ?? null,
      billing: cart?.billing_address ?? null
    };
  }, [cartData]);

  // Find customer address that matches cart address by comparing fields
  const matchingShippingAddress = useMemo(() => {
    if (!cartAddresses.shipping) return undefined;
    return addresses.find((a) => addressFieldsMatch(cartAddresses.shipping, a));
  }, [addresses, cartAddresses.shipping]);

  const matchingBillingAddress = useMemo(() => {
    if (!cartAddresses.billing) return undefined;
    return addresses.find((a) => addressFieldsMatch(cartAddresses.billing, a));
  }, [addresses, cartAddresses.billing]);

  // Find default addresses
  const defaultShippingAddress = useMemo(
    () => addresses.find((a) => a.is_default_shipping),
    [addresses]
  );

  const defaultBillingAddress = useMemo(
    () => addresses.find((a) => a.is_default_billing),
    [addresses]
  );

  // Initialize form with defaults
  const form = useForm<CheckoutAddressFormData>({
    resolver: zodResolver(checkoutAddressSchema),
    defaultValues: {
      email: profile.email,
      phone: profile.phone || "",
      shippingAddressId: "",
      shippingAddress: undefined,
      sameAsShipping: true,
      billingAddressId: "",
      billingAddress: undefined
    }
  });

  // Track if form has been initialized to prevent re-initialization loops
  const hasInitializedRef = useRef(false);

  // Update form defaults when data loads (runs only once after data is ready)
  // Priority: matching cart address > default addresses > cart address as inline
  useEffect(() => {
    // Skip if still loading
    if (addressesLoading || profileLoading || cartLoading) {
      return;
    }

    // Also wait for addresses data to be available (even if empty array)
    if (!addressesData) {
      return;
    }

    // Skip if already initialized
    if (hasInitializedRef.current) {
      return;
    }

    // Mark as initialized BEFORE making any changes
    hasInitializedRef.current = true;

    // Determine shipping address to pre-select:
    // 1. Find customer address matching cart's shipping address fields
    // 2. Fall back to default shipping address
    // 3. If cart has address but no match, use it as inline "Other" address
    const hasCartShippingAddress = cartAddresses.shipping !== null;
    const addressToSelect = matchingShippingAddress ?? defaultShippingAddress;

    // Determine billing address handling
    const cartBillingMatchesCartShipping =
      cartAddresses.shipping &&
      cartAddresses.billing &&
      addressFieldsMatch(cartAddresses.billing, {
        first_name: cartAddresses.shipping.first_name,
        last_name: cartAddresses.shipping.last_name,
        address_1: cartAddresses.shipping.address_1,
        city: cartAddresses.shipping.city,
        country_code: cartAddresses.shipping.country_code,
        postal_code: cartAddresses.shipping.postal_code
      } as HttpTypes.StoreCustomerAddress);

    const billingToSelect = matchingBillingAddress ?? defaultBillingAddress;
    const selectedShippingId = addressToSelect?.id;
    const selectedBillingId = billingToSelect?.id;

    const shouldBeSameAsShipping =
      !selectedBillingId ||
      selectedBillingId === selectedShippingId ||
      cartBillingMatchesCartShipping;

    // Build the complete form values object
    const updatedFields: Partial<CheckoutAddressFormData> = {};

    // Set shipping address
    if (addressToSelect) {
      updatedFields.shippingAddressId = addressToSelect.id;
      if (addressToSelect.phone) {
        updatedFields.phone = addressToSelect.phone;
      }
    } else if (hasCartShippingAddress) {
      const inlineShipping = cartAddressToInline(cartAddresses.shipping);
      if (inlineShipping) {
        updatedFields.shippingAddressId = "";
        updatedFields.shippingAddress = inlineShipping;
        if (inlineShipping.phone) {
          updatedFields.phone = inlineShipping.phone;
        }
      }
    } else if (defaultShippingAddress) {
      updatedFields.shippingAddressId = defaultShippingAddress.id;
      if (defaultShippingAddress.phone) {
        updatedFields.phone = defaultShippingAddress.phone;
      }
    }

    // The phone this checkout already wrote to the cart wins over the saved
    // address's and the profile's: it is the one the buyer typed for this
    // order, and a saved address or profile commonly has none. When the phone
    // was read only from the saved address, the payment step's fresh form came
    // up with an empty phone and failed validation on a field that step does
    // not show, so Place order silently did nothing (KAN-230).
    if (cartAddresses.shipping?.phone) {
      updatedFields.phone = cartAddresses.shipping.phone;
    }

    // Set billing address
    if (shouldBeSameAsShipping) {
      updatedFields.sameAsShipping = true;
      updatedFields.billingAddressId = "";
    } else if (billingToSelect && selectedBillingId) {
      updatedFields.sameAsShipping = false;
      updatedFields.billingAddressId = selectedBillingId;
    } else if (cartAddresses.billing) {
      const inlineBilling = cartAddressToInline(cartAddresses.billing);
      if (inlineBilling) {
        updatedFields.sameAsShipping = false;
        updatedFields.billingAddressId = "";
        updatedFields.billingAddress = inlineBilling;
      }
    }
    const setValue = form.setValue;

    // Use setValue for each field to ensure registered inputs get updated
    // This is more reliable than reset() when inputs register after the hook runs
    Object.entries(updatedFields).forEach(([name, value]) => {
      setValue(name as keyof CheckoutAddressFormData, value, {
        // shouldDirty: true, // Optional: mark as dirty
        shouldValidate: false // Optional: trigger validation
        // shouldTouch: true // Optional: mark as touched
      });
    });
  }, [
    addressesLoading,
    profileLoading,
    cartLoading,
    addressesData,
    form.setValue
  ]);

  // Watch form values (do this once, not inside useMemo dependencies)
  const sameAsShipping = form.watch("sameAsShipping");
  const shippingAddressId = form.watch("shippingAddressId");
  const billingAddressId = form.watch("billingAddressId");

  // Store form.setValue in a ref to avoid dependency issues
  const setValueRef = useRef(form.setValue);
  setValueRef.current = form.setValue;

  useEffect(() => {
    if (sameAsShipping && shippingAddressId) {
      // When "same as shipping" is on, clear the billing selection
      // The actual address used will be the shipping address
      setValueRef.current("billingAddressId", "", { shouldValidate: false });
    }
  }, [sameAsShipping, shippingAddressId]);

  // Get selected address objects (use watched values, not form.watch in deps)
  const selectedShippingAddress = useMemo(
    () => addresses.find((a) => a.id === shippingAddressId),
    [addresses, shippingAddressId]
  );

  const selectedBillingAddress = useMemo(() => {
    if (sameAsShipping) {
      return selectedShippingAddress;
    }
    return addresses.find((a) => a.id === billingAddressId);
  }, [addresses, sameAsShipping, selectedShippingAddress, billingAddressId]);

  const isLoading = addressesLoading || profileLoading || cartLoading;

  return {
    form,
    addresses,
    defaultShippingAddress,
    defaultBillingAddress,
    isLoading,
    hasNoAddresses: !isLoading && addresses.length === 0,
    selectedShippingAddress,
    selectedBillingAddress
  };
}
